import { brand, radius } from '@alison/ui';
import ChevronLeftOutlined from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlined from '@mui/icons-material/ChevronRightOutlined';
import ImageNotSupportedOutlined from '@mui/icons-material/ImageNotSupportedOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { alpha, type Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { formatDuration } from '../../../lib/dateUtils.js';
import { GemCard, type GemPicks } from '../GemCard.js';
import { CHECKBOX_SX } from '../Pill.js';
import {
  cellOf,
  columnsOf,
  deltaOf,
  formatValue,
  type Metric,
  metricDeltaOf,
  type ResultRow,
  spendOf,
  type TableColumn,
} from '../resultRows.js';
import type { ResultSelection } from '../workflows.types.js';
import {
  type ComparisonResult,
  featureKeyOf,
  featureOptionsOf,
  hookScenesOf,
  overviewOf,
  type TagRow,
  tagRowsOf,
  tagsOf,
  type ComparisonView as View,
  viewRowsOf,
} from './comparisonResult.js';

const LIST_SIZE = 5;
const TILE_THUMB = 64;
const FATIGUE_THUMB = 28;
// Views sit side by side only where the container (page or drawer) fits them;
// each section is its own grid row, so a view's sections start level with the other's.
const WIDE = '@container (min-width: 900px)';
const SIDE_COLORS = ['primary.main', brand.purple];
const PANEL_RADIUS = `${radius.xl}px`;
// Side by side, a view's cells join into one panel; stacked, each cell is its own card.
const PANEL_CELL_SX = {
  gap: 2,
  minWidth: 0,
  px: 4,
  py: 3,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: PANEL_RADIUS,
  bgcolor: 'background.paper',
};
const TILE_SX = {
  p: 2,
  borderRadius: PANEL_RADIUS,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  minWidth: 0,
};
const EMPTY_SX = {
  ...TILE_SX,
  flex: 1,
  minHeight: 56,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderStyle: 'dashed',
  bgcolor: 'transparent',
  color: 'text.disabled',
};
const LABEL_SX = { fontSize: 11, color: 'text.secondary' };
const TILES_SX = {
  display: 'grid',
  gridTemplateColumns: `repeat(${LIST_SIZE}, minmax(0, 1fr))`,
  gap: 2,
};
const THUMB_SX = {
  position: 'relative',
  width: '100%',
  borderRadius: `${radius.md}px`,
  overflow: 'hidden',
  bgcolor: 'background.page',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const DURATION_SX = (theme: Theme) => ({
  position: 'absolute',
  right: 4,
  bottom: 4,
  px: 0.8,
  borderRadius: `${radius.sm}px`,
  fontSize: 9,
  fontWeight: 700,
  color: 'common.white',
  bgcolor: alpha(theme.palette.common.black, 0.5),
});
const TILE_CHECKBOX_SX = (theme: Theme) => ({
  position: 'absolute',
  top: 4,
  left: 4,
  borderRadius: `${radius.sm}px`,
  bgcolor: alpha(theme.palette.background.paper, 0.85),
});
const TILE_TEXT_SX = { fontSize: 11, minWidth: 0 };
const CARD_SX = {
  gap: 2,
  px: 4,
  py: 3,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: PANEL_RADIUS,
  bgcolor: 'background.paper',
};
// Equal outer columns centre the plot on the card, level with the views' split above.
const HOOK_ROW_SX = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 3fr) minmax(0, 1fr)',
  alignItems: 'stretch',
  minHeight: 40,
};
const HOOK_TICKS = 4;
const MARK_SIZE = 10;
const GEM_SX = { borderRadius: PANEL_RADIUS };
const SELECT_SX = { maxWidth: 360, '& .MuiOutlinedInput-root': { borderRadius: PANEL_RADIUS } };
// Stacked, a cell names its view; side by side, the header row already does.
const CAPTION_SX = {
  fontSize: 11,
  fontWeight: 600,
  color: 'text.secondary',
  [WIDE]: { display: 'none' },
};

type Labels = Record<string, string>;
type Edge = 'first' | 'middle' | 'last';
type ListOf = (rows: ResultRow[], view: View, selection: ResultSelection | undefined) => ReactNode;

interface HookGap {
  side: number;
  percent: number;
}

interface HookAxis {
  lo: number;
  hi: number;
  ticks: number[];
}

interface ComparisonViewProps {
  result: ComparisonResult;
  pool: ResultRow[];
  labels: Labels;
  selection?: ResultSelection;
  elementPicks?: GemPicks;
  onOpen?: (row: ResultRow) => void;
}

function columnsSx(count: number) {
  return {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    columnGap: 3,
    rowGap: 2,
    [WIDE]: { gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` },
  };
}

function edgeSx(edge: Edge) {
  const top = edge === 'first' ? PANEL_RADIUS : '0px';
  const bottom = edge === 'last' ? PANEL_RADIUS : '0px';
  return {
    [WIDE]: {
      borderTopWidth: edge === 'first' ? '1px' : '0px',
      borderTopLeftRadius: top,
      borderTopRightRadius: top,
      borderBottomLeftRadius: bottom,
      borderBottomRightRadius: bottom,
    },
  };
}

function formatted(metric: Metric | null): string {
  return metric === null
    ? '—'
    : formatValue(metric.value, metric.format, metric.currencySymbol ?? '$', metric.precision);
}

function SectionTitle({ children }: { children: ReactNode }): ReactNode {
  return <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{children}</Typography>;
}

function Section({
  id,
  title,
  action,
  children,
}: {
  id: string;
  title: string | undefined;
  action?: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <Stack data-testid={`section-${id}`} sx={{ gap: 1.5, minWidth: 0 }}>
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between', minHeight: 34 }}
      >
        <SectionTitle>{title}</SectionTitle>
        {action}
      </Stack>
      {children}
    </Stack>
  );
}

function Cell({
  id,
  view,
  captioned,
  children,
}: {
  id: string;
  view: View;
  captioned: boolean;
  children: ReactNode;
}): ReactNode {
  return (
    <Stack data-testid={`cell-${id}-${view.key}`} sx={{ gap: 1, minWidth: 0 }}>
      {captioned ? <Typography sx={CAPTION_SX}>{view.label}</Typography> : null}
      {children}
    </Stack>
  );
}

function PanelRow({ id, count, children }: { id: string; count: number; children: ReactNode }) {
  return (
    <Box data-testid={`section-${id}`} sx={[columnsSx(count), { [WIDE]: { rowGap: 0 } }]}>
      {children}
    </Box>
  );
}

function PanelCell({
  id,
  view,
  edge,
  captioned,
  title,
  action,
  children,
}: {
  id: string;
  view: View;
  edge: Edge;
  captioned: boolean;
  title?: string | undefined;
  action?: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <Stack data-testid={`cell-${id}-${view.key}`} sx={[PANEL_CELL_SX, edgeSx(edge)]}>
      {captioned ? <Typography sx={CAPTION_SX}>{view.label}</Typography> : null}
      {title === undefined ? null : (
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between', minHeight: 30 }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{title}</Typography>
          {action}
        </Stack>
      )}
      {children}
    </Stack>
  );
}

function Empty({ id, view }: { id: string; view: View }): ReactNode {
  return (
    <Box data-testid={`empty-${id}-${view.key}`} sx={EMPTY_SX}>
      —
    </Box>
  );
}

function Tile({
  label,
  value,
  note,
}: {
  label: string | undefined;
  value: string;
  note?: ReactNode;
}): ReactNode {
  return (
    <Stack sx={{ gap: 0.5, minWidth: 0 }}>
      <Typography sx={LABEL_SX}>{label}</Typography>
      <Typography sx={{ fontSize: 18, fontWeight: 600 }}>{value}</Typography>
      {note}
    </Stack>
  );
}

function Overview({ view, labels }: { view: View; labels: Labels }): ReactNode {
  const { spend, kpi } = overviewOf(view);
  const delta = kpi === null ? null : metricDeltaOf(kpi);
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.5 }}>
      <Tile label={labels['spend']} value={formatted(spend)} />
      <Tile
        label={labels['kpi']}
        value={formatted(kpi)}
        note={
          delta === null ? null : (
            <Typography
              sx={{ fontSize: 11, color: delta.isImprovement ? 'success.dark' : 'error.main' }}
            >
              {delta.text} {labels['vs_dataset']}
            </Typography>
          )
        }
      />
      <Tile label={labels['creative_count']} value={String(view.creative_count)} />
    </Box>
  );
}

function sectionSelection(
  rows: ResultRow[],
  selection: ResultSelection | undefined,
): ResultSelection | undefined {
  if (selection === undefined) return undefined;
  const ids = rows.map((row) => row.id);
  const selectedIds = selection.selectedIds.filter((id) => ids.includes(id));
  return {
    selectedIds,
    onToggle: selection.onToggle,
    onToggleAll: () => {
      const all = selectedIds.length === ids.length;
      for (const id of ids) if (all || !selectedIds.includes(id)) selection.onToggle(id);
    },
  };
}

// The figure a list ranks by is the view's own KPI, wherever the rows carry it.
function kpiColumnOf(view: View, rows: ResultRow[]): TableColumn | undefined {
  const name = overviewOf(view).kpi?.metric;
  const columns = columnsOf(rows);
  return columns.find((column) => column.name === name) ?? columns[0];
}

function Thumb({ row, size }: { row: ResultRow; size: number }): ReactNode {
  const [failed, setFailed] = useState(false);
  const duration = size === TILE_THUMB ? formatDuration(row.duration) : null;
  return (
    <Box sx={{ ...THUMB_SX, height: size }}>
      {row.thumbnailUrl === null || failed ? (
        <ImageNotSupportedOutlined sx={{ fontSize: size * 0.35, color: 'text.disabled' }} />
      ) : (
        <Box
          component="img"
          src={row.thumbnailUrl}
          alt=""
          data-print-thumbnail="true"
          onError={() => setFailed(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      {duration === null ? null : <Box sx={DURATION_SX}>{duration}</Box>}
    </Box>
  );
}

function OpenableThumb({
  row,
  size,
  onOpen,
}: {
  row: ResultRow;
  size: number;
  onOpen: ((row: ResultRow) => void) | undefined;
}): ReactNode {
  if (onOpen === undefined || row.creativeId === null) return <Thumb row={row} size={size} />;
  return (
    <ButtonBase
      aria-label={row.label}
      onClick={() => onOpen(row)}
      sx={{ display: 'block', width: '100%', borderRadius: `${radius.md}px` }}
    >
      <Thumb row={row} size={size} />
    </ButtonBase>
  );
}

function RowCheckbox({
  row,
  selection,
  sx,
}: {
  row: ResultRow;
  selection: ResultSelection | undefined;
  sx?: typeof TILE_CHECKBOX_SX;
}): ReactNode {
  if (selection === undefined) return null;
  return (
    <Checkbox
      size="small"
      checked={selection.selectedIds.includes(row.id)}
      onChange={() => selection.onToggle(row.id)}
      sx={[CHECKBOX_SX, ...(sx === undefined ? [] : [sx])]}
      slotProps={{ input: { 'aria-label': `Select ${row.label}` } }}
    />
  );
}

function CreativeTiles({
  rows,
  figure,
  selection,
  onOpen,
}: {
  rows: ResultRow[];
  figure: (row: ResultRow) => string | null;
  selection: ResultSelection | undefined;
  onOpen: ((row: ResultRow) => void) | undefined;
}): ReactNode {
  return (
    <Box sx={TILES_SX}>
      {rows.map((row) => {
        const value = figure(row);
        return (
          <Stack key={row.id} data-testid={`tile-${row.id}`} sx={{ gap: 1, minWidth: 0 }}>
            <Box sx={{ position: 'relative' }}>
              <OpenableThumb row={row} size={TILE_THUMB} onOpen={onOpen} />
              <RowCheckbox row={row} selection={selection} sx={TILE_CHECKBOX_SX} />
            </Box>
            <Tooltip title={row.label}>
              <Typography noWrap sx={TILE_TEXT_SX}>
                {row.label}
              </Typography>
            </Tooltip>
            {value === null ? null : (
              <Typography sx={{ ...TILE_TEXT_SX, fontWeight: 600, color: 'text.secondary' }}>
                {value}
              </Typography>
            )}
          </Stack>
        );
      })}
    </Box>
  );
}

function FatigueLines({
  rows,
  column,
  selection,
  onOpen,
}: {
  rows: ResultRow[];
  column: TableColumn | undefined;
  selection: ResultSelection | undefined;
  onOpen: ((row: ResultRow) => void) | undefined;
}): ReactNode {
  return (
    <Stack>
      {rows.map((row) => {
        const delta = column === undefined ? null : deltaOf(row, column);
        return (
          <Stack
            key={row.id}
            direction="row"
            data-testid={`fatigue-${row.id}`}
            sx={{ gap: 2, py: 1.2, alignItems: 'center', minWidth: 0 }}
          >
            <RowCheckbox row={row} selection={selection} />
            <Box sx={{ width: FATIGUE_THUMB, flexShrink: 0 }}>
              <OpenableThumb row={row} size={FATIGUE_THUMB} onOpen={onOpen} />
            </Box>
            <Tooltip title={row.label}>
              <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: 12 }}>
                {row.label}
              </Typography>
            </Tooltip>
            {column === undefined || delta === null ? null : (
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  color: delta.isImprovement ? 'success.dark' : 'error.main',
                }}
              >
                {column.label} {cellOf(row, column)} · {delta.text}
              </Typography>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}

function SelectAll({
  selection,
  count,
}: {
  selection: ResultSelection | undefined;
  count: number;
}): ReactNode {
  if (selection === undefined || count === 0) return null;
  const picked = selection.selectedIds.length;
  return (
    <Checkbox
      size="small"
      checked={picked === count}
      indeterminate={picked > 0 && picked < count}
      onChange={selection.onToggleAll}
      sx={CHECKBOX_SX}
      slotProps={{ input: { 'aria-label': 'Select all' } }}
    />
  );
}

function pagesOf(rows: ResultRow[]): ResultRow[][] {
  const pages: ResultRow[][] = [];
  for (let start = 0; start < rows.length; start += LIST_SIZE) {
    pages.push(rows.slice(start, start + LIST_SIZE));
  }
  return pages;
}

function Pager({
  page,
  count,
  onPage,
}: {
  page: number;
  count: number;
  onPage: (page: number) => void;
}): ReactNode {
  return (
    <Stack direction="row" sx={{ alignItems: 'center' }}>
      <IconButton
        size="small"
        aria-label="Previous"
        disabled={page === 0}
        onClick={() => onPage(page - 1)}
      >
        <ChevronLeftOutlined fontSize="small" />
      </IconButton>
      <Typography sx={{ ...LABEL_SX, minWidth: 28, textAlign: 'center' }}>
        {page + 1} / {count}
      </Typography>
      <IconButton
        size="small"
        aria-label="Next"
        disabled={page === count - 1}
        onClick={() => onPage(page + 1)}
      >
        <ChevronRightOutlined fontSize="small" />
      </IconButton>
    </Stack>
  );
}

// Every page stays mounted so the track can slide; pages out of view are inert.
function Carousel({ page, children }: { page: number; children: ReactNode[] }): ReactNode {
  return (
    <Box sx={{ overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          transform: `translateX(-${page * 100}%)`,
          transition: 'transform 250ms ease',
        }}
      >
        {children.map((content, index) => (
          <Box
            // biome-ignore lint/suspicious/noArrayIndexKey: a page is its position in the list
            key={index}
            inert={index !== page}
            data-testid="carousel-page"
            sx={{ flex: '0 0 100%', minWidth: 0 }}
          >
            {content}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function CreativeCell({
  id,
  title,
  edge,
  view,
  captioned,
  rows,
  selection,
  listOf,
}: {
  id: string;
  title: string | undefined;
  edge: Edge;
  view: View;
  captioned: boolean;
  rows: ResultRow[];
  selection: ResultSelection | undefined;
  listOf: ListOf;
}): ReactNode {
  const [chosen, setPage] = useState(0);
  const pages = pagesOf(rows);
  const page = Math.min(chosen, Math.max(0, pages.length - 1));
  const shown = pages[page] ?? [];
  const scoped = sectionSelection(shown, selection);
  return (
    <PanelCell
      id={id}
      view={view}
      edge={edge}
      captioned={captioned}
      title={title}
      action={
        <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center' }}>
          <SelectAll selection={scoped} count={shown.length} />
          {pages.length > 1 ? <Pager page={page} count={pages.length} onPage={setPage} /> : null}
        </Stack>
      }
    >
      {pages.length === 0 ? (
        <Empty id={id} view={view} />
      ) : (
        <Carousel page={page}>
          {pages.map((rowsOnPage) =>
            listOf(rowsOnPage, view, sectionSelection(rowsOnPage, selection)),
          )}
        </Carousel>
      )}
    </PanelCell>
  );
}

function CreativeSection({
  id,
  title,
  edge,
  views,
  lists,
  selection,
  listOf,
}: {
  id: string;
  title: string | undefined;
  edge: Edge;
  views: View[];
  lists: ResultRow[][];
  selection: ResultSelection | undefined;
  listOf: ListOf;
}): ReactNode {
  return (
    <PanelRow id={id} count={views.length}>
      {views.map((view, index) => (
        <CreativeCell
          key={view.key}
          id={id}
          title={title}
          edge={edge}
          view={view}
          captioned={views.length > 1}
          rows={lists[index] ?? []}
          selection={selection}
          listOf={listOf}
        />
      ))}
    </PanelRow>
  );
}

function TagGrid({
  views,
  rows,
  picks,
}: {
  views: View[];
  rows: TagRow[];
  picks: GemPicks | undefined;
}): ReactNode {
  return (
    <Stack sx={{ gap: 2 }}>
      {rows.map((row) => (
        <Box key={row.key} data-testid={`tag-${row.key}`} sx={columnsSx(views.length)}>
          {views.map((view, index) => {
            const gem = row.gems[index] ?? null;
            const id = `tag-${row.key}`;
            return (
              <Cell key={view.key} id={id} view={view} captioned={views.length > 1}>
                {gem === null ? (
                  <Empty id={id} view={view} />
                ) : (
                  <GemCard gem={gem} sx={GEM_SX} {...(picks === undefined ? {} : { picks })} />
                )}
              </Cell>
            );
          })}
        </Box>
      ))}
    </Stack>
  );
}

// The higher view's lead over the lower: "higher", not "better", since some KPIs read low-is-good.
function gapOf(a: number | null, b: number | null): HookGap | null {
  if (a === null || b === null) return null;
  const high = Math.max(a, b);
  const low = Math.min(a, b);
  if (low <= 0) return null;
  return { side: a >= b ? 0 : 1, percent: (high / low - 1) * 100 };
}

// The second view is a ring, not a dot: the two side colours are near-identical to deutan vision.
function SideMark({ index }: { index: number }): ReactNode {
  const color = SIDE_COLORS[index] ?? 'text.disabled';
  return (
    <Box
      sx={{
        width: MARK_SIZE,
        height: MARK_SIZE,
        flexShrink: 0,
        boxSizing: 'border-box',
        borderRadius: '50%',
        boxShadow: (theme: Theme) => `0 0 0 2px ${theme.palette.background.paper}`,
        ...(index === 0
          ? { bgcolor: color }
          : { bgcolor: 'background.paper', border: '2.5px solid', borderColor: color }),
      }}
    />
  );
}

// A few round-numbered ticks spanning every value, so the plot reads against labelled lines.
function axisOf(values: number[]): HookAxis | null {
  if (values.length === 0) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const rough = (max - min || Math.abs(max) || 1) / HOOK_TICKS;
  const power = 10 ** Math.floor(Math.log10(rough));
  const step = ([1, 2, 5, 10].find((m) => m * power >= rough) ?? 10) * power;
  const lo = Math.floor(min / step) * step;
  const hi = Math.max(Math.ceil(max / step) * step, lo + step);
  const ticks: number[] = [];
  for (let tick = lo; tick <= hi + step / 2; tick += step) ticks.push(Number(tick.toPrecision(12)));
  return { lo, hi, ticks };
}

function xOf(axis: HookAxis, value: number): number {
  return ((value - axis.lo) / (axis.hi - axis.lo)) * 100;
}

function Gridlines({ axis }: { axis: HookAxis }): ReactNode {
  return axis.ticks.map((tick) => (
    <Box
      key={tick}
      sx={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: `${xOf(axis, tick)}%`,
        borderLeft: '1px dashed',
        borderColor: 'divider',
      }}
    />
  ));
}

function HookScenes({ views, rows }: { views: View[]; rows: TagRow[] }): ReactNode {
  const kpi = views.map((view) => overviewOf(view).kpi?.metric).find((name) => name !== undefined);
  const metricOf = (gem: TagRow['gems'][number]) =>
    gem?.metrics.find((metric) => kpi === undefined || metric.metric === kpi) ?? null;
  const valuesOf = (row: TagRow) => row.gems.map((gem) => metricOf(gem)?.value ?? null);
  const nameOf = (row: TagRow) => {
    const named = row.gems.find((gem) => gem !== null);
    return `${named?.feature}: ${named?.tag}`;
  };

  if (views.length !== 2) {
    return (
      <Stack sx={{ gap: 1 }}>
        {rows.map((row) => (
          <Stack key={row.key} direction="row" data-testid={`hook-${row.key}`} sx={{ gap: 2 }}>
            <Typography sx={{ flex: 1, fontSize: 12 }}>{nameOf(row)}</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
              {row.gems.map((gem) => formatted(metricOf(gem))).join(' · ')}
            </Typography>
          </Stack>
        ))}
      </Stack>
    );
  }

  const gaps = rows.map((row) => {
    const [a = null, b = null] = valuesOf(row);
    return gapOf(a, b);
  });
  const axis = axisOf(rows.flatMap((row) => valuesOf(row).filter((value) => value !== null)));
  const sample = rows.flatMap((row) => row.gems.map(metricOf)).find((metric) => metric !== null);
  const tickLabel = (tick: number) =>
    sample == null ? String(tick) : formatted({ ...sample, value: tick });
  return (
    <Stack>
      <Box sx={{ ...HOOK_ROW_SX, minHeight: 24 }}>
        <Box />
        <Stack direction="row" sx={{ gap: 2, justifyContent: 'center', alignItems: 'center' }}>
          {views.map((view, index) => (
            <Stack
              key={view.key}
              direction="row"
              sx={{ gap: 0.75, alignItems: 'center', minWidth: 0 }}
            >
              <SideMark index={index} />
              <Typography noWrap sx={{ fontSize: 11, color: 'text.secondary', minWidth: 0 }}>
                {view.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
        <Box />
      </Box>
      <Box data-testid="hook-axis" sx={{ ...HOOK_ROW_SX, minHeight: 28 }}>
        <Typography sx={{ ...LABEL_SX, fontWeight: 600, alignSelf: 'end', pb: 0.5 }}>
          {kpi}
        </Typography>
        <Box sx={{ position: 'relative', mx: 1.5 }}>
          {axis?.ticks.map((tick) => (
            <Typography
              key={tick}
              sx={{
                ...LABEL_SX,
                position: 'absolute',
                bottom: 4,
                left: `${xOf(axis, tick)}%`,
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap',
              }}
            >
              {tickLabel(tick)}
            </Typography>
          ))}
        </Box>
        <Box />
      </Box>
      {rows.map((row, rowIndex) => {
        const gap = gaps[rowIndex] ?? null;
        const values = valuesOf(row);
        const present = values.filter((value) => value !== null);
        return (
          <Box key={row.key} data-testid={`hook-${row.key}`} sx={HOOK_ROW_SX}>
            <Tooltip title={nameOf(row)}>
              <Typography noWrap sx={{ fontSize: 12, alignSelf: 'center', pr: 3, minWidth: 0 }}>
                {nameOf(row)}
              </Typography>
            </Tooltip>
            <Box sx={{ position: 'relative', mx: 1.5 }}>
              {axis === null ? null : (
                <>
                  <Gridlines axis={axis} />
                  {present.length < 2 ? null : (
                    <Box
                      data-testid={`hook-span-${row.key}`}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        height: 2,
                        transform: 'translateY(-50%)',
                        left: `${xOf(axis, Math.min(...present))}%`,
                        width: `${xOf(axis, Math.max(...present)) - xOf(axis, Math.min(...present))}%`,
                        bgcolor: 'text.disabled',
                      }}
                    />
                  )}
                  {views.map((view, index) => {
                    const metric = metricOf(row.gems[index] ?? null);
                    const value = metric?.value ?? null;
                    if (value === null) return null;
                    return (
                      <Tooltip key={view.key} title={`${view.label} · ${formatted(metric)}`}>
                        <Box
                          data-testid={`hook-${row.key}-${view.key}`}
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: `${xOf(axis, value)}%`,
                            transform: 'translate(-50%, -50%)',
                            p: 0.75,
                            display: 'flex',
                          }}
                        >
                          <SideMark index={index} />
                        </Box>
                      </Tooltip>
                    );
                  })}
                </>
              )}
            </Box>
            <Stack sx={{ alignItems: 'flex-end', justifyContent: 'center' }}>
              {gap === null ? null : (
                <Stack direction="row" sx={{ gap: 0.75, alignItems: 'center' }}>
                  <SideMark index={gap.side} />
                  <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                    +{Math.round(gap.percent)}%
                  </Typography>
                </Stack>
              )}
              <Typography sx={{ fontSize: 10, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                {row.gems.map((gem) => formatted(metricOf(gem))).join(' · ')}
              </Typography>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}

export function ComparisonView({
  result,
  pool,
  labels,
  selection,
  elementPicks,
  onOpen,
}: ComparisonViewProps): ReactNode {
  const { views } = result;
  const features = featureOptionsOf(result);
  const recommended =
    result.recommended_feature === null ? '' : featureKeyOf(result.recommended_feature);
  const [feature, setFeature] = useState(recommended);
  const lists = views.map((view) => viewRowsOf(view, pool));
  const hooks = tagRowsOf(views.map(hookScenesOf));

  return (
    <Stack sx={{ gap: 4, containerType: 'inline-size' }} data-testid="comparison">
      <Stack sx={{ gap: 2, [WIDE]: { gap: 0 } }} data-testid="comparison-panels">
        <PanelRow id="heading" count={views.length}>
          {views.map((view, index) => (
            <Stack
              key={view.key}
              data-testid={`view-${view.key}`}
              sx={[PANEL_CELL_SX, edgeSx('first')]}
            >
              <Stack direction="row" sx={{ gap: 1.25, alignItems: 'center', minWidth: 0 }}>
                <SideMark index={index} />
                <Typography variant="h3" noWrap sx={{ fontSize: 16, minWidth: 0 }}>
                  {view.label}
                </Typography>
              </Stack>
              {view.flags.map((flag) => (
                <Alert key={flag} severity="warning">
                  {labels[`flag.${flag}`]}
                </Alert>
              ))}
            </Stack>
          ))}
        </PanelRow>

        <PanelRow id="overview" count={views.length}>
          {views.map((view) => (
            <PanelCell
              key={view.key}
              id="overview"
              view={view}
              edge="middle"
              captioned={views.length > 1}
            >
              <Overview view={view} labels={labels} />
            </PanelCell>
          ))}
        </PanelRow>

        <CreativeSection
          id="top_spenders"
          title={labels['top_spenders']}
          edge="middle"
          views={views}
          lists={lists.map((rows) => rows.spenders)}
          selection={selection}
          listOf={(rows, _view, scoped) => (
            <CreativeTiles rows={rows} figure={spendOf} selection={scoped} onOpen={onOpen} />
          )}
        />
        <CreativeSection
          id="top_performers"
          title={labels['top_performers']}
          edge="middle"
          views={views}
          lists={lists.map((rows) => rows.performers)}
          selection={selection}
          listOf={(rows, view, scoped) => {
            const column = kpiColumnOf(view, rows);
            return (
              <CreativeTiles
                rows={rows}
                figure={(row) => (column === undefined ? null : cellOf(row, column))}
                selection={scoped}
                onOpen={onOpen}
              />
            );
          }}
        />
        <CreativeSection
          id="fatigued"
          title={labels['fatigued']}
          edge="last"
          views={views}
          lists={lists.map((rows) => rows.fatigued)}
          selection={selection}
          listOf={(rows, view, scoped) => (
            <FatigueLines
              rows={rows}
              column={kpiColumnOf(view, rows)}
              selection={scoped}
              onOpen={onOpen}
            />
          )}
        />
      </Stack>

      {features.length === 0 ? null : (
        <Section id="feature_comparison" title={labels['feature_comparison']}>
          <TextField
            select
            size="small"
            value={feature}
            onChange={(event) => setFeature(event.target.value)}
            slotProps={{ select: { displayEmpty: true } }}
            sx={SELECT_SX}
          >
            <MenuItem value="" disabled>
              {labels['no_recommended_feature']}
            </MenuItem>
            {features.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.feature} · {option.scene}
              </MenuItem>
            ))}
          </TextField>
          {feature === '' ? null : (
            <TagGrid
              views={views}
              rows={tagRowsOf(views.map((view) => tagsOf(view, feature)))}
              picks={elementPicks}
            />
          )}
        </Section>
      )}

      {hooks.length === 0 ? null : (
        <Stack sx={CARD_SX}>
          <Section id="hook_scenes" title={labels['hook_scenes']}>
            <HookScenes views={views} rows={hooks} />
          </Section>
        </Stack>
      )}
    </Stack>
  );
}
